import axios from "axios";
import api from "../config/ApiService";
import { apiToken, sowStudioApi } from "../config/ApiService";

const DEFAULT_HUBSPOT_PORTAL_ID = '51754235';

const normalizeSowDealPreviewHubspotLink = (payload: any) => {
    if (!payload || typeof payload !== 'object') {
        return payload;
    }

    const rawLink = typeof payload.hubspot_link === 'string' ? payload.hubspot_link.trim() : '';
    if (!rawLink || typeof payload.deal_id !== 'number') {
        return payload;
    }

    const normalizedRecordLink = `https://app.hubspot.com/contacts/${DEFAULT_HUBSPOT_PORTAL_ID}/record/0-3/${payload.deal_id}`;
    const recordMatch = rawLink.match(/\/contacts\/(\d+)\/record\/0-3\/(\d+)(?:[/?#]|$)/i);
    if (recordMatch) {
        const [, portalId, recordDealId] = recordMatch;
        if (portalId === DEFAULT_HUBSPOT_PORTAL_ID && Number(recordDealId) === payload.deal_id) {
            return payload;
        }

        return {
            ...payload,
            hubspot_link: normalizedRecordLink,
        };
    }

    if (/\/deal\/\d+(?:[/?#]|$)/i.test(rawLink)) {
        return {
            ...payload,
            hubspot_link: normalizedRecordLink,
        };
    }

    return payload;
};

// Function to fetch the authentication token
export const fetchToken = async (code: string) => {
    try {
        const response = await api.post<any>('/auth/token', { code });

        if (response && response.data) {
            return response;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get details of the logged-in user
export const getLoggedInUserDetails = async (deal_id: number | null = null) => {
    try {
        let response;
        if (deal_id) {
            response = await apiToken.get<any>(`/pricing_tool/users/me?deal_id=${deal_id}`);
        } else {
            response = await apiToken.get<any>(`/pricing_tool/users/me`);
        }

        if (response && response.data) {
            return response;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get HubSpot deal details by deal ID
export const getHubspotDealDetails = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/hubspot_deal_details?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get SOW Studio deal details by deal ID (includes SOW-specific fields)
export const getSowDealDetails = async (deal_id: number) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(`/pricing_tool/sow_deal_details?deal_id=${deal_id}`);

        if (response && response.data) {
            return normalizeSowDealPreviewHubspotLink(response.data);
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const searchSowDeals = async (query: string, limit: number = 20) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(
            `/pricing_tool/sow_deal_search?query=${encodeURIComponent(query)}&limit=${limit}`
        );
        if (response && response.data) {
            return response.data;
        }
        throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        throw error;
    }
};

// Function to get the "My SOWs" list — deals this user has opened/edited in SOW Studio
export const getRecentSowIntakes = async (limit: number = 4) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(`/pricing_tool/sow_intake/recent?limit=${limit}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getSowDealFileUrl = async (file_id: string, deal_id: number | string) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(
            `/pricing_tool/sow_deal_file_url?file_id=${encodeURIComponent(file_id)}&deal_id=${encodeURIComponent(String(deal_id))}`
        );

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get the persisted SOW Studio intake record (prompt, snapshot, materials) for a deal
export const getSowIntake = async (deal_id: number) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(`/pricing_tool/sow_intake?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to upsert the SOW Studio manual intake fields (billing terms, end users,
// additional contexts, prompt text) for a deal
export const saveSowIntake = async (payload: {
    deal_id: number;
    billing_schedule?: Array<{ month: number; amount: number | null }>;
    payment_milestones?: Array<{ milestone: string; amount: number | null; invoice_trigger: string; target_date?: string | null }>;
    billing_terms?: string | null;
    client_signing_contact?: string | null;
    blend_signatory?: { name: string; title?: string; email: string; legal_entity?: string } | null;
    end_users?: string[];
    additional_contexts?: string[];
    prompt_text?: string | null;
    sow_document_type?: string | null;
    manual_overrides?: Record<string, string>;
}) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.put<any>(`/pricing_tool/sow_intake`, payload);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to capture the one-time HubSpot deal preview snapshot onto the SOW record.
// Idempotent on the backend — safe to call every time the deal preview loads.
export const captureSowIntakeSnapshot = async (deal_id: number) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.post<any>(`/pricing_tool/sow_intake/snapshot?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to request a pre-signed S3 POST for a SOW intake material upload
export const presignSowMaterialUpload = async (payload: { deal_id: number; file_name: string; content_type: string }) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.post<any>(`/pricing_tool/sow_intake/materials/presign-upload`, payload);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Uploads a file directly to S3 using the url/fields returned by presignSowMaterialUpload.
// This call goes straight to S3, not our backend — no auth header, the pre-signed
// policy fields ARE the authorization.
export const uploadFileToPresignedS3Post = async (url: string, fields: Record<string, string>, file: File) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    try {
        await axios.post(url, formData);
        return { success: true };
    } catch (error: any) {
        console.error('Error uploading file to S3:', error?.response?.data || error.message);
        return { error: 'Failed to upload the file to S3.' };
    }
};

// Function to persist metadata for a SOW intake material already uploaded to S3
export const confirmSowMaterial = async (payload: {
    deal_id: number;
    document_type: string;
    file_name: string;
    s3_key: string;
    file_size?: number;
    content_type?: string;
}) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.post<any>(`/pricing_tool/sow_intake/materials/confirm`, payload);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to link an external supporting document against the SOW record
export const linkSowMaterial = async (payload: { deal_id: number; document_type: string; file_name: string; url: string }) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.post<any>(`/pricing_tool/sow_intake/materials/link`, payload);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to resolve a SOW intake material to a downloadable URL
export const getSowMaterialDownloadUrl = async (deal_id: number, material_id: string | number) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.get<any>(`/pricing_tool/sow_intake/materials/${material_id}/download-url?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to delete one SOW intake material (S3 object, if any, + metadata row)
export const deleteSowMaterial = async (deal_id: number, material_id: string | number) => {
    try {
        const sowDealClient = process.env.REACT_APP_SOW_STUDIO_API_URL ? sowStudioApi : apiToken;
        const response = await sowDealClient.delete<any>(`/pricing_tool/sow_intake/materials?deal_id=${deal_id}&material_id=${material_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get pricing contributors by deal ID
export const getPricingContributors = async (deal_id: number | null = null) => {
    try {
        let response;
        if (deal_id) {
            response = await apiToken.get<any>(`/pricing_tool/pricing_contributors?deal_id=${deal_id}`);
        } else {
            response = await apiToken.get<any>(`/pricing_tool/pricing_contributors`);
        }

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get pricing version list by deal ID
export const getPricingVersion = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_calculator_version_list?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getAllPricingVersions = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/all_pricing_versions`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get pricing version list by deal ID
export const getDealVersionDetails = async (deal_id: number, versionName: string) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_deal_version_details?deal_id=${deal_id}&version_name=${encodeURIComponent(versionName)}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getPricingCalculatorDetails = async (deal_id: number | null = null, version_id: number | null = null) => {
    try {
        let response
        if (deal_id && version_id) {
            response = await apiToken.get<any>(`/pricing_tool/pricing_calculator_details?deal_id=${deal_id}&version_id=${version_id}`);
        }else if (version_id){
            response = await apiToken.get<any>(`/pricing_tool/pricing_calculator_details?version_id=${version_id}`);
        } 
        else {
            response = await apiToken.get<any>(`/pricing_tool/pricing_calculator_details`);
        }

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getNonResourcePricingDetails = async (deal_id: number | null = null, version_id: number | null = null) => {
    try {
        let response
        if (deal_id && version_id) {
            response = await apiToken.get<any>(`/pricing_tool/non_resource_pricing_details?deal_id=${deal_id}&version_id=${version_id}`);
        } else if (version_id) {
            response = await apiToken.get<any>(`/pricing_tool/non_resource_pricing_details?version_id=${version_id}`);
        }
        else {
            response = await apiToken.get<any>(`/pricing_tool/non_resource_pricing_details`);
        }

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getPricingDeals = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_deals`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getHomePageStatistics = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/statistics`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getDealsLastSyncInfo = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/hubspot_deals_sync_info`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getPricingDealVersions = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_deal_versions?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getPricingDealVersionCount = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_deal_version_count?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getWorkHours = async (deal_id: number | null = null, version_id: Number | null = null) => {
    try {
        let response;
        const params: string[] = [];
        if (deal_id) params.push(`deal_id=${deal_id}`);
        if (version_id) params.push(`version_id=${version_id}`);
        const queryString = params.length > 0 ? `?${params.join('&')}` : '';
        response = await apiToken.get<any>(`/pricing_tool/work_hours${queryString}`);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getHubspotDeals = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/hubspot_deals`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getAllDealsInfo = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/all_deals`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getDiscountTargetMarginDD = async (deal_id: number | null = null) => {
    try {
        let response;
        if (deal_id) {
            response = await apiToken.get<any>(`/pricing_tool/discount_target_margin_dd?deal_id=${deal_id}`);
        } else {
            response = await apiToken.get<any>(`/pricing_tool/discount_target_margin_dd`);
        }
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const createPricingDeal = async (deal_id: number) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/create_pricing_deal?deal_id=${deal_id}`, { deal_id });

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const createPricingDealFromTemplate = async (deal_id: number, template_id: number) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/create_pricing_deal_from_template?deal_id=${deal_id}&template_id=${template_id}`, { deal_id, template_id });

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const cloneDealVersion = async (deal_id: number, curr_version_name: string, clone_version_name: string) => {
    try {
        const request: any = {}
        request['version_name'] = curr_version_name
        request['clone_version_name'] = clone_version_name

        const response = await apiToken.post<any>(`/pricing_tool/clone_pricing_deal_version?deal_id=${deal_id}`, request);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const savePricingDealVersion = async (deal_id: number,
    version_name: string,
    project_discount: number,
    discount_rationale: string,
    fixed_fee_contingency: number,
    hubspot_deal_details: any,
    participants_rate_data: any,
    resource_allocation_plan: any,
    deal_economics_summaries: any,
    send_resource_request_email: boolean,
    lock_ode: boolean,
    additional_resourcing_info: string,
    currency: string,
    update_hubspot_deal_amount: boolean,
    revenue_data: any = [],
    monthly_summary_data: any = [],
    monthly_summary_data_after_discount: any = [],
    currency_symbol: string = "$",
    non_resource_data: any = [],
    resource_request_confirmation?: { preview_token: string, mappings: any[] }
) => {
    try {
        const request: any = {}
        request['deal_id'] = deal_id
        request['version_name'] = version_name
        request['send_resource_request_email'] = send_resource_request_email
        request['lock_ode'] = lock_ode
        request['project_discount'] = project_discount
        request['discount_rationale'] = discount_rationale
        request['fixed_fee_contingency'] = fixed_fee_contingency
        request['pricing_contributors'] = hubspot_deal_details.pricing_contributors
        request['base_price_version_id'] = hubspot_deal_details.base_price_version_id
        request['additional_resourcing_info'] = additional_resourcing_info
        request['currency'] = currency
        request['update_hubspot_deal_amount'] = update_hubspot_deal_amount
        request['participants_rate_data'] = participants_rate_data.map((item: any) => ({
            "key": item.key,
            "resource_type": item.resourceType,
            "region": item.selectRegion,
            "service_line": item.serviceLineCapability,
            "function": item.function,
            "career_level": item.selectLevel,
            "cost_per_hour": item.loadedLaborCostHr,
            "target_client_margin": item.targetClientMargin,
            "proposed_price_per_hour": item.proposedPriceHr,
            "resource_requirement_id": item.resourceRequirementId ?? null,
        }))
        request['resource_allocation_plan'] = resource_allocation_plan.map((item: any) => ({
            "key": item.key,
            "allocation": item.allocation,
            "start_date": item.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            "end_date": item.endDate.format('YYYY-MM-DDTHH:mm:ss'),
            "duration": item.duration,
            "monthly_wise_allocation": item.months,
            "name": item.name,
            "comment": item.comment
        }))
        request['deal_economics_summaries'] = deal_economics_summaries.map((item: any) => ({
            "blend_hourly_price": item.blendHourlyPrice ?? 0,
            "total_project_price": item.totalProjectPrice ?? 0,
            "project_contingency_amount": item.projectcontingencyAmount ?? 0,
            "project_discount_amount": item.projectDiscountAmount ?? 0,
            "net_project_price": item.netProjectPrice ?? 0,
            "total_project_labor_cost": item.totalProjectLaborCost ?? 0,
            "project_client_margin": item.projectClientMargin ? parseFloat(item.projectClientMargin.replace('%', '')) : 0,
            "target_client_margin": item.targetClientMargin ? parseFloat(item.targetClientMargin.replace('%', '')) : 0,
            "variance_to_target_margin": item.varianceToTargetMargin ? parseFloat(item.varianceToTargetMargin.replace('%', '')) : 0,
            "total_billable_hours": item.totalBillableHours ?? 0,
            "below_target_margin_reason": item.belowTargetMarginReason ?? "",
            "deal_pricing_comment": item.dealPricingComment ?? "",
        }))[0] ?? {}

        request['revenue_data'] = revenue_data
        request['monthly_summary_data'] = monthly_summary_data
        request['monthly_summary_data_after_discount'] = monthly_summary_data_after_discount
        request['currency_symbol'] = currency_symbol
        if (resource_request_confirmation) {
            request['resource_request_preview_token'] = resource_request_confirmation.preview_token
            request['resource_request_mappings'] = resource_request_confirmation.mappings
        }
        request['non_resource_data'] = non_resource_data.map((item: any) => ({
            "key": item.key,
            "region": item.selectRegion,
            "service_line": item.serviceLineCapability,
            "function": item.function,
            "monthly_cost": item.monthlyCost,
            "target_client_margin": item.targetClientMargin,
            "start_date": item.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            "end_date": item.endDate.format('YYYY-MM-DDTHH:mm:ss'),
        }))

        const response = await apiToken.post<any>(`/pricing_tool/save_pricing_deal_version?deal_id=${deal_id}`, request);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        const detail = error?.response?.data?.detail;
        if (detail?.code === 'resource_request_confirmation_required' && detail?.preview) {
            return {
                confirmation_required: true,
                preview: detail.preview,
                error_status: error?.response?.status,
            };
        }
        return {
            "error": typeof detail === 'string' ? detail : detail?.message || "Unknown error occurred",
            "error_status": error?.response?.status || "No status available"
        };
    }
};


export const updatePricingDealVersionName = async (deal_id: number, version_name: string, new_version_name: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/update_pricing_deal_version_name?deal_id=${deal_id}`, {
            "old_version_name": version_name,
            "new_version_name": new_version_name
        });

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getLatestLineItems = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/get_latest_line_items?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const updateHubspotLineItems = async (deal_id: number, new_hubspot_line_items: any, version_id: number, version_name: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/update_line_items_to_hubspot`, {"deal_id": deal_id, "version_id": version_id, "version_name": version_name, "line_items": new_hubspot_line_items});
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const lockPricingDeal = async (deal_id: number, version_name: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/lock_pricing_deal?deal_id=${deal_id}&version_name=${version_name}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const UnlockPricingDeal = async (deal_id: number, unlock_reason: string, unlock_comments: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/unlock_pricing_deal?deal_id=${deal_id}`, { "unlock_reason": unlock_reason, 'unlock_comments': unlock_comments });

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const createNewPricingDealVersion = async (deal_id: number, version_name: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/create_new_pricing_deal_version`, {
            "deal_id": deal_id,
            "version_name": version_name
        });

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getPricingTemplates = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_template_list`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const copyPricingTemplate = async (templateId: string, templateName: string): Promise<{ template_id: number; template_name: string }> => {
    const response = await apiToken.post('/pricing_tool/copy_pricing_template',
        { template_name: templateName }, { params: { template_id: templateId } });
    return response.data;
};

export const deletePricingTemplate = async (templateId: string): Promise<void> => {
    await apiToken.post('/pricing_tool/delete_pricing_template', null, {
        params: { template_id: templateId },
    });
};

export const createPricingTemplate = async (data: any) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/create_pricing_template`, data);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const pricingTemplateDetails = async (template_id: any) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_template_details?template_id=${template_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

// Function to get pricing contributors by deal ID
export const getHubspotUsers = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/hubspot_users`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const createTemplateFromVersion = async (deal_id: number, version_name: string, template_name: string, template_description: string) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/create_template_from_version`, {
            deal_id, version_name, template_name, template_description
        });
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const savePricingTemplate = async (template_id: number,
    project_discount: number,
    discount_rationale: string,
    fixed_fee_contingency: number,
    template_details: any,
    participants_rate_data: any,
    resource_allocation_plan: any,
    deal_economics_summaries: any,
    non_resource_data: any[] = []) => {
    try {
        const request: any = {}
        request['template_id'] = template_id
        request['non_resource_data'] = non_resource_data.map(item => ({
            key: item.key, region: item.selectRegion, service_line: item.serviceLineCapability,
            function: item.function, monthly_cost: item.monthlyCost, target_client_margin: item.targetClientMargin,
            start_date: item.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            end_date: item.endDate.format('YYYY-MM-DDTHH:mm:ss'),
        }))
        request['engagement_type'] = template_details['Engagement Type']
        request['project_start_date'] = template_details['Expected Start Date']
        request['duration_in_months'] = template_details['Duration']
        request['template_name'] = template_details['Template Name']
        request['template_description'] = template_details['Template Description']
        request['project_discount'] = project_discount
        request['discount_rationale'] = discount_rationale
        request['fixed_fee_contingency'] = fixed_fee_contingency
        request['pricing_contributors'] = template_details['Pricing Contributors']
        request['base_price_version_id'] = template_details['Base Price Version ID']
        request['participants_rate_data'] = participants_rate_data.map((item: any) => ({
            "key": item.key,
            "resource_type": item.resourceType,
            "region": item.selectRegion,
            "service_line": item.serviceLineCapability,
            "function": item.function,
            "career_level": item.selectLevel,
            "cost_per_hour": item.loadedLaborCostHr,
            "target_client_margin": item.targetClientMargin,
            "proposed_price_per_hour": item.proposedPriceHr,
        }))
        request['resource_allocation_plan'] = resource_allocation_plan.map((item: any) => ({
            "key": item.key,
            "allocation": item.allocation,
            "start_date": item.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            "end_date": item.endDate.format('YYYY-MM-DDTHH:mm:ss'),
            "duration": item.duration,
            "monthly_wise_allocation": item.months,
            "name": item.name,
            "comment": item.comment
        }))
        request['deal_economics_summaries'] = deal_economics_summaries.map((item: any) => ({
            "blend_hourly_price": item.blendHourlyPrice,
            "total_project_price": item.totalProjectPrice,
            "project_contingency_amount": item.projectcontingencyAmount,
            "project_discount_amount": item.projectDiscountAmount,
            "net_project_price": item.netProjectPrice,
            "total_project_labor_cost": item.totalProjectLaborCost,
            "project_client_margin": parseFloat(item.projectClientMargin.replace('%', '')),
            "target_client_margin": parseFloat(item.targetClientMargin.replace('%', '')),
            "variance_to_target_margin": parseFloat(item.varianceToTargetMargin.replace('%', '')),
            "total_billable_hours": item.totalBillableHours,
            "below_target_margin_reason": item.belowTargetMarginReason,
            "deal_pricing_comment": item.dealPricingComment,
        }))[0]

        const response = await apiToken.post<any>(`/pricing_tool/save_pricing_template?template_id=${template_id}`, request);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


// Function to refresh deals(s)
export const refreshDeals = async (deal_id: number | null = null, refresh_from: string | null = null) => {
    try {
        let response;
        const request: any = {}
        if (deal_id) {
            request['deal_id'] = deal_id
        }
        if (refresh_from) {
            request['sync_from'] = refresh_from
        }
        response = await apiToken.post<any>(`/pricing_tool/sync_deals_from_hubspot`, request);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


// Function to get service line names
export const GetServiceLineList = async (deal_id: number | null = null) => {
    try {
        let response;
        response = await apiToken.get<any>(`/pricing_tool/service_line_list?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const UnlockODEPricingDeal = async (
  deal_id: number,
  unlock_reason: string,
  version_name: string,
  unlock_comments: string
) => {
  try {
    const response = await apiToken.post<any>(
      `/pricing_tool/unlock_ode_request`,
      {
        deal_id: deal_id,
        unlock_req_reason: unlock_reason,
        version_name: version_name,
        unlock_comments: unlock_comments
      }
    );

    if (response && response.data) {
      return response.data;
    } else {
      throw new Error("Response is undefined or doesn't contain data");
    }
  } catch (error: any) {
    console.error(
      "Error Response Data:",
      error?.response?.data || error.message
    );
    console.error(
      "Error Response Status:",
      error?.response?.status || "No status available"
    );
    return {
      error: error?.response?.data?.detail || "Unknown error occurred",
      error_status: error?.response?.status || "No status available",
    };
  }
};

export const getAllResourceRequestsData = async () => {
    try {
        let response = await apiToken.get<any>(`/pricing_tool/get_resource_requests`);

        if (response && response.data) {
            return response;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getResourceRequestedDealsData = async () => {
    try {
        let response = await apiToken.get<any>(`/pricing_tool/resource_requested_deals`);

        if (response && response.data) {
            return response;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


export const getPricingDealRMODetails = async (dealId: string, versionId: string) => {
    try {
        let response = await apiToken.get<any>(`/pricing_tool/pricing_deal_rmo_details?deal_id=${dealId}&version_id=${versionId}`);

        if (response && response.data) {
            return response;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const generateResourceSuggestions = async (
    dealId: string, request:any
) => {
    try {
        const response = await apiToken.post<any>(
            `/pricing_tool/employee_suggestions?deal_id=${dealId}`,
            request
        );

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error(
            "Error Response Data:",
            error?.response?.data || error.message
        );
        console.error(
            "Error Response Status:",
            error?.response?.status || "No status available"
        );
        return {
            error: error?.response?.data?.detail || "Unknown error occurred",
            error_status: error?.response?.status || "No status available",
        };
    }
};


export const getGeneratedResourceSuggestionsFromCache = async (
    dealId: string,
) => {
    try {
        const response = await apiToken.post<any>(
            `/pricing_tool/employee_suggestions_from_cache?deal_id=${dealId}`
        );

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        return {
            error: error?.response?.data?.detail || "Unknown error occurred",
            error_status: error?.response?.status || "No status available",
        };
    }
};

export const getPricingCalculatorVersionList = async (deal_id: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/pricing_calculator_version_list?deal_id=${deal_id}`);

        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        console.error('Error Response Status:', error?.response?.status || "No status available");
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};


// OpenAir Bookings APIs
export const getOpenAirUsers = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/get_openair_users`);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getDealBookings = async (dealId: number, nsProjectId?: string) => {
    try {
        let url = `/pricing_tool/get_deal_bookings?deal_id=${dealId}`;
        if (nsProjectId) url += `&ns_project_id=${nsProjectId}`;
        const response = await apiToken.get<any>(url);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const getBookingTypes = async () => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/get_booking_types`);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const previewOpenAirBookings = async (dealId: number, versionId: number) => {
    try {
        const response = await apiToken.get<any>(`/pricing_tool/preview_openair_bookings?deal_id=${dealId}&version_id=${versionId}`);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const saveOpenAirBookings = async (payload: any) => {
    try {
        const response = await apiToken.post<any>(`/pricing_tool/save_openair_bookings`, payload);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};

export const previewResourceRequest = async (
    deal_id: number,
    participants_rate_data: any[],
    resource_allocation_plan: any[]
) => {
    try {
        const request = {
            participants_rate_data: participants_rate_data.map((item: any) => ({
                key: item.key,
                resource_type: item.resourceType,
                region: item.selectRegion,
                service_line: item.serviceLineCapability,
                function: item.function,
                career_level: item.selectLevel,
                cost_per_hour: item.loadedLaborCostHr,
                target_client_margin: item.targetClientMargin,
                proposed_price_per_hour: item.proposedPriceHr,
                resource_requirement_id: item.resourceRequirementId ?? null,
            })),
            resource_allocation_plan: resource_allocation_plan.map((item: any) => ({
                key: item.key,
                allocation: item.allocation,
                start_date: item.startDate.format('YYYY-MM-DDTHH:mm:ss'),
                end_date: item.endDate.format('YYYY-MM-DDTHH:mm:ss'),
                duration: item.duration,
                monthly_wise_allocation: item.months,
                name: item.name,
                comment: item.comment,
            })),
        }
        const response = await apiToken.post<any>(
            `/pricing_tool/resource_request_preview?deal_id=${deal_id}`,
            request
        )
        return response.data
    } catch (error: any) {
        return {
            error: error?.response?.data?.detail || 'Unable to compare resource requirements',
            error_status: error?.response?.status,
        }
    }
}

export const deleteOpenAirBooking = async (bookingId?: number, oaBookingId?: string) => {
    try {
        const params = new URLSearchParams();
        if (bookingId) params.append("booking_id", bookingId.toString());
        if (oaBookingId) params.append("oa_booking_id", oaBookingId);
        const response = await apiToken.delete<any>(`/pricing_tool/delete_openair_booking?${params.toString()}`);
        if (response && response.data) {
            return response.data;
        } else {
            throw new Error("Response is undefined or doesn't contain data");
        }
    } catch (error: any) {
        console.error('Error Response Data:', error?.response?.data || error.message);
        return { "error": error?.response?.data?.detail || "Unknown error occurred", "error_status": error?.response?.status || "No status available" };
    }
};
